import { updateField } from 'vuex-map-fields'
import { getNewId } from '@/utils/id'
import BaseStore from '@/store/baseStore'
import Pagination from '@/store/pagination'
import api from '@/api/api'
import { REVISION_KEY } from '@/components/constant'
import API from '@/api/apiUrls'
import {
  createLinkElementThenDownloadFile,
  getDownloadFileNameFromHeaders
} from '@/utils/downloadFile'

export const namespaced = true

const baseUrl = API.BOUNDARY_CONDITION.BURSTING_PARAMETER.INDEX

const baseStore = new BaseStore(baseUrl)
const pagination = new Pagination(baseUrl)

const getDefaultState = () => {
  return {
    ...structuredClone(baseStore.state),
    ...structuredClone(pagination.state),
    availableReferencePoints: [],
    importLoading: false,
    downloadLoading: false,
    exportLoading: false,
    importItems: [],
    importItemCount: 0,
    templateFileName: 'Bursting_parameter.xlsx'
  }
}
export const state = getDefaultState()

export const mutations = {
  ...baseStore.mutations,
  ...pagination.mutations,
  RESET_ITEM_STATE(state) {
    state.items = []
    state.dirtyItem = []
  },
  RESET_STATE(state) {
    Object.assign(state, getDefaultState())
  },
  SET_ITEMS(state, items) {
    state.items = items
  },
  TOGGLE_UPLOAD_LOADING(state) {
    state.uploadLoading = !state.uploadLoading
  },
  TOGGLE_IMPORT_LOADING(state) {
    state.importLoading = !state.importLoading
  },
  TOGGLE_DOWNLOAD_LOADING(state) {
    state.downloadLoading = !state.downloadLoading
  },
  TOGGLE_EXPORT_LOADING(state) {
    state.exportLoading = !state.exportLoading
  },
  SET_IMPORT_ITEMS(state, items) {
    state.importItems = items
  },
  SET_IMPORT_ITEM_COUNT(state, count) {
    state.importItemCount = count
  },
  SET_LOADING(state, payload) {
    state.loading = payload
  },
  SET_COUNT(state, count) {
    state.count = count
  },
  RESET_IMPORT_STATE(state) {
    state.importItems = []
    state.importItemCount = 0
    state.uploadLoading = false
    state.importLoading = false
  },
  SET_AVAILABLE_REFERENCE_POINTS(state, items) {
    state.availableReferencePoints = items
  },
  ADD_AVAILABLE_REFERENCE_POINT(state, item) {
    state.availableReferencePoints.push(item)
  },
  REMOVE_AVAILABLE_REFERENCE_POINT(state, items) {
    items.forEach((item) => {
      const index = state.availableReferencePoints.indexOf(item)
      if (index > -1) {
        state.availableReferencePoints.splice(index, 1)
      }
    })
  },
  EDIT_AVAILABLE_ITEMS_REFERENCE_POINT(state, { oldId, newId }) {
    if (oldId === null) {
      const index = state.availableReferencePoints.indexOf(newId)
      if (index > -1) {
        state.availableReferencePoints.splice(index, 1)
      }
    } else {
      let index = state.availableReferencePoints.findIndex((item) => item === newId)
      state.availableReferencePoints[index] = oldId
    }
  },
  updateField
}
export const actions = {
  ...baseStore.actions,
  ...pagination.actions,
  resetState({ commit }) {
    commit('RESET_STATE')
  },
  resetItemState({ commit }) {
    commit('RESET_ITEM_STATE')
  },
  resetImportState(context) {
    context.commit('RESET_IMPORT_STATE')
  },
  addAvailableReferencePoint({ commit }, id) {
    commit('ADD_AVAILABLE_REFERENCE_POINT', id)
  },
  removeAvailableReferencePoint({ commit }, ids) {
    commit('REMOVE_AVAILABLE_REFERENCE_POINT', ids)
  },
  editAvailableItemsReferencePoint({ commit }, { oldId, newId }) {
    commit('EDIT_AVAILABLE_ITEMS_REFERENCE_POINT', { oldId, newId })
  },
  async fetchPaginated(context, revisionId) {
    context.commit('SET_LOADING', true)
    const itemsPerPage = 10
    let url = `${context.state.baseUrl}?page_size=${itemsPerPage}&search=${revisionId}`
    let response
    if (context.state.serverPagination && context.state.serverPagination.next) {
      response = await api.get(context.state.serverPagination.next)
    } else if (
      !context.state.serverPagination ||
      context.state.serverPagination.count > context.state.items.length
    ) {
      response = await api.get(url)
      context.commit('ADD_COUNT', response['data'].count)
    } else {
      context.commit('SET_LOADING', false)
      return
    }
    context.commit('ADD_ITEMS', response['data']['results'])
    context.commit('SET_SERVER_PAGINATION', response['data'])
    context.commit('SET_LOADING', false)
  },
  async fetchAvailableReferencePointById(context, selectedRevisionId) {
    let url = `${baseUrl}available_reference_point/${selectedRevisionId}/`
    let response = await api.get(url)
    context.commit('SET_AVAILABLE_REFERENCE_POINTS', response['data'])
  },
  async addNewItem(context) {
    let id = getNewId(context.state.items, 'id')
    let newItem = context.getters.getEmptyBurstingParameter(id)
    context.commit('ADD_ITEM', newItem)
    context.commit('ADD_COUNT', 1)
  },

  async import(context) {
    let newObjs = context.state.importItems
    const revision_id = context.rootGetters['project/selectedRevision'](
      REVISION_KEY.BURSTING_PARAMETER
    )?.id
    const url = API.BOUNDARY_CONDITION.BURSTING_PARAMETER.IMPORT(revision_id)
    if (newObjs.length) {
      context.commit('TOGGLE_IMPORT_LOADING')
      try {
        const response = await api.post(url, newObjs)
        context.commit('SET_ITEMS', response.data)
        context.commit('SET_COUNT', response.data.length)
      } catch (e) {
        console.log(e)
        context.commit('TOGGLE_IMPORT_LOADING')
        throw e
      }
      context.commit('TOGGLE_IMPORT_LOADING')
    }
  },

  async uploadFile(context, formData) {
    const revision_id = context.rootGetters['project/selectedRevision'](
      REVISION_KEY.BURSTING_PARAMETER
    )?.id
    formData.append('revision_id', revision_id)
    context.commit('TOGGLE_UPLOAD_LOADING')
    try {
      const baseUrl = API.BOUNDARY_CONDITION.BURSTING_PARAMETER.BULK_UPLOAD
      const response = await api.post(baseUrl, formData)
      context.commit('SET_IMPORT_ITEMS', response.data)
      context.commit('SET_IMPORT_ITEM_COUNT', response.data.length)
      context.commit('TOGGLE_UPLOAD_LOADING')
    } catch {
      context.commit('TOGGLE_UPLOAD_LOADING')
    }
  },

  async downloadTemplate(context) {
    context.commit('TOGGLE_DOWNLOAD_LOADING')
    try {
      const projectId = context.rootGetters['project/selectedProjectId']
      const url = API.BOUNDARY_CONDITION.BURSTING_PARAMETER.TEMPLATE(projectId)
      const { data, headers } = await api.get(url, {
        responseType: 'blob'
      })
      createLinkElementThenDownloadFile(
        data,
        getDownloadFileNameFromHeaders(headers) || 'bursting_parameter_template.xlsx'
      )
      context.commit('TOGGLE_DOWNLOAD_LOADING')
      return data
    } catch (e) {
      console.log(e)
      context.commit('TOGGLE_DOWNLOAD_LOADING')
      return null
    }
  },

  async exportData(context) {
    context.commit('TOGGLE_EXPORT_LOADING')
    try {
      const selectedRevisionId = context.rootGetters['project/selectedRevision'](
        REVISION_KEY.BURSTING_PARAMETER
      ).id
      const url = API.BOUNDARY_CONDITION.BURSTING_PARAMETER.EXPORT(selectedRevisionId)
      const { data, headers } = await api.get(url, {
        responseType: 'blob'
      })
      createLinkElementThenDownloadFile(
        data,
        getDownloadFileNameFromHeaders(headers) || 'bursting_parameter_export.xlsx'
      )
      context.commit('TOGGLE_EXPORT_LOADING')
      return data
    } catch (e) {
      console.log(e)
      context.commit('TOGGLE_EXPORT_LOADING')
      return null
    }
  }
}

export const getters = {
  getEmptyBurstingParameter: (state, getters, rootState, rootGetters) => (id) => {
    let emptyObj = {}
    Object.keys(state.options).forEach((key) => {
      emptyObj[key] = state.options[key].default !== undefined ? state.options[key].default : null
    })
    emptyObj.id = id
    emptyObj.reference_point = null
    emptyObj.bursting_parameter_revision = rootGetters['project/selectedRevision'](
      REVISION_KEY.BURSTING_PARAMETER
    ).id
    return emptyObj
  },
  getExportFileName: (state, getters, rootState) => {
    let projectName = rootState['project'].currentProject?.name
    return projectName + '_BurstingParameter_export.xlsx'
  },
  getItems: (state) => {
    let burstingParameter = []
    state.items.forEach((referencePoint) =>
      burstingParameter.push(...referencePoint.burstingparameter_set)
    )
    return burstingParameter
  }
}
